/* eslint-disable max-lines */
import { createApi } from '@reduxjs/toolkit/query/react';
import { BenefitType, ReferentPriceType } from '@strata/constants';
import { formatNameLastFirst } from '@strata/helper-functions';
import { GridWithFilters } from '@strata/react-grid';
import { FirstFillStatusIcon } from '@strata/react-status-icons';
import { ReferralStatusIndicator } from '@strata/react-status-indicators';
import { SuccessToast } from '@strata/react-toast-message';
import { ComponentProps } from 'react';

import { PERFORMANCE_API_URL } from 'api/APIBase';
import { handleError, handleRTKQueryError } from 'services/Error';
import { getBaseQuery } from 'store/api/apiBaseQuery';

export interface ReferralValueCase {
    referral_id: string;
    value_case_id: string;
    value_case_name: string;
    value_case_description: string;
    incentive: number;
    strat_rule_id: string;
    strat_rule_name: string;
}

export interface WonReferralSummary {
    referral_id: string;
    referral_start: string;
    benefit: BenefitType;
    first_fill_status?: (typeof FirstFillStatusIcon.Statuses)[keyof typeof FirstFillStatusIcon.Statuses];
    first_name?: string;
    friendly_id: string;
    incentive_total?: number;
    is_brand: boolean;
    last_name?: string;
    member_id?: string;
    product_name: string;
    referral_status: (typeof ReferralStatusIndicator.Statuses)[keyof typeof ReferralStatusIndicator.Statuses];
    segment_name: string;
    outreach_date?: string;
    value_cases: Pick<ReferralValueCase, 'value_case_name' | 'strat_rule_name'>[];
    has_copay_assistance: boolean;
    // Needed for filter category typing, but not in actual payload
    segment_id?: never;
    value_case_name?: never;
}

export interface ExpiredReferral {
    referralId: string;
    payerId: string;
    supplierNpi: string;
    product: string;
    memberId?: string;
    healthPlan: string;
    segmentName: string;
    patient: string;
    incentiveTotal?: number;
    referralStatus: (typeof ReferralStatusIndicator.Statuses)[keyof typeof ReferralStatusIndicator.Statuses];
    referralStart: string;
    referralExpiration: string;
    friendlyId: string;
    benefit: BenefitType;
    isBrand: boolean;
    valueCases: ReferralValueCase[];
    referentPriceType: ReferentPriceType;
    hasCopayAssistance: boolean;
}

export interface DeclinedReferral {
    referralId: string;
    product: string;
    isBrand: boolean;
    patient: string;
    memberDOB: string;
    segmentName: string;
    benefit: BenefitType;
    offerRate?: string;
    referentPriceType: ReferentPriceType;
    reason: string;
    upn?: string;
    declineTimestamp?: string;
}

export const AutomationChangeTypes = {
    ADDED: 'Added',
    DELETED: 'Deleted',
    TURNED_ON: 'Turned On',
    TURNED_OFF: 'Turned Off',
    CHANGED: 'Changed',
} as const;
export type AutomationChangeType =
    (typeof AutomationChangeTypes)[keyof typeof AutomationChangeTypes];

export interface AutomationRuleHistory {
    id: string;
    username: string;
    changeType: AutomationChangeType;
    timestamp: string;
}

const TagType = {
    NEEDS_BY_DATE: 'NEEDS_BY_DATE',
    REFERRALS: 'REFERRALS',
} as const;

export const performanceApi = createApi({
    baseQuery: getBaseQuery(PERFORMANCE_API_URL, true),
    reducerPath: 'performanceApi',
    tagTypes: Object.values(TagType),
    refetchOnMountOrArgChange: true,
    endpoints: (build) => ({
        getPagedReferrals: build.query<
            {
                count?: number;
                referrals: WonReferralSummary[];
                filterOptions: Record<string, string[]>;
            },
            {
                page: number;
                searchText?: string;
                activeFilters?: ComponentProps<
                    typeof GridWithFilters<WonReferralSummary>
                >['activeFilters'];
                sort?: ComponentProps<typeof GridWithFilters<WonReferralSummary>>['sortProperties'];
            }
        >({
            query: ({ page, searchText, sort, ...rest }) => ({
                url: '/referrals/paged',
                params: {
                    page,
                    search_text: searchText,
                    sort_key: sort?.[0].id,
                    sort_desc: sort?.[0].desc,
                    ...rest,
                },
            }),
            providesTags: [TagType.REFERRALS],
            transformResponse: (response: {
                count?: number;
                referrals: WonReferralSummary[];
                filter_options: Record<string, string[]>;
            }) => ({
                count: response?.count,
                referrals: response?.referrals,
                filterOptions: response?.filter_options,
            }),
            transformErrorResponse: handleRTKQueryError('An error occurred retrieving referrals.'),
        }),
        getReferral: build.query<
            {
                referralId: string;
                payerId: string;
                incentiveTotal?: number;
                referralExpiration: string;
                valueCases: ReferralValueCase[];
            },
            string
        >({
            query: (referralId) => `/referrals/${referralId}`,
            transformResponse: (response: {
                referral_id: string;
                payer_id: string;
                incentive_total?: number;
                referral_expiration: string;
                value_cases: ReferralValueCase[];
            }) => ({
                referralId: response.referral_id,
                payerId: response.payer_id,
                incentiveTotal: response.incentive_total,
                referralExpiration: response.referral_expiration,
                valueCases: response.value_cases,
            }),
            transformErrorResponse: (error) => {
                if (error.status !== 404) {
                    handleError('Error retrieving referral', error);
                }
            },
        }),
        getExpiredReferrals: build.query<ExpiredReferral[], void>({
            query: () => '/referrals/expired',
            transformResponse: (
                response: {
                    referral_id: string;
                    payer_id: string;
                    supplier_npi: string;
                    product_name: string;
                    member_id?: string;
                    health_plan: string;
                    segment_name: string;
                    first_name: string;
                    last_name: string;
                    incentive_total?: number;
                    referral_status: (typeof ReferralStatusIndicator.Statuses)[keyof typeof ReferralStatusIndicator.Statuses];
                    referral_start: string;
                    referral_expiration: string;
                    friendly_id: string;
                    benefit: BenefitType;
                    is_brand: boolean;
                    value_cases: ReferralValueCase[];
                    referent_price_type: ReferentPriceType;
                    has_copay_assistance: boolean;
                }[],
            ) =>
                response?.map((referral) => ({
                    referralId: referral.referral_id,
                    payerId: referral.payer_id,
                    supplierNpi: referral.supplier_npi,
                    product: referral.product_name,
                    memberId: referral.member_id,
                    healthPlan: referral.health_plan,
                    segmentName: referral.segment_name,
                    patient: formatNameLastFirst(referral.last_name, referral.first_name),
                    incentiveTotal: referral.incentive_total,
                    referralStart: referral.referral_start,
                    referralStatus: referral.referral_status,
                    referralExpiration: referral.referral_expiration,
                    friendlyId: referral.friendly_id,
                    benefit: referral.benefit,
                    isBrand: referral.is_brand,
                    valueCases: referral.value_cases,
                    referentPriceType: referral.referent_price_type,
                    hasCopayAssistance: referral.has_copay_assistance,
                })),
            transformErrorResponse: handleRTKQueryError(
                'An error occurred retrieving expired referrals.',
            ),
        }),
        getDeclinedReferrals: build.query<DeclinedReferral[], void>({
            query: () => '/referrals/declined',
            transformResponse: (
                response: {
                    referral_id: string;
                    product_name: string;
                    is_brand: boolean;
                    first_name: string;
                    last_name: string;
                    member_dob: string;
                    segment_name: string;
                    benefit: BenefitType;
                    offer_rate?: string;
                    referent_price_type: ReferentPriceType;
                    reason: string;
                    upn?: string;
                    decline_timestamp: string;
                }[],
            ) =>
                response?.map((referral) => ({
                    referralId: referral.referral_id,
                    product: referral.product_name,
                    isBrand: referral.is_brand,
                    patient: formatNameLastFirst(referral.last_name, referral.first_name),
                    memberDOB: referral.member_dob,
                    segmentName: referral.segment_name,
                    benefit: referral.benefit,
                    offerRate: referral.offer_rate,
                    referentPriceType: referral.referent_price_type,
                    reason: referral.reason,
                    upn: referral.upn,
                    declineTimestamp: referral.decline_timestamp,
                })),
            transformErrorResponse: handleRTKQueryError(
                'An error occurred retrieving declined referrals.',
            ),
        }),
        getAutomationRuleHistory: build.query<AutomationRuleHistory[], string>({
            query: (ruleId) => `/automation_changes/${ruleId}`,
            transformResponse: (
                response: {
                    change_id: string;
                    username: string;
                    change_type: AutomationChangeType;
                    change_timestamp: string;
                }[],
            ) =>
                response.map((change) => ({
                    id: change.change_id,
                    username: change.username,
                    changeType: change.change_type,
                    timestamp: change.change_timestamp,
                })),
            transformErrorResponse: handleRTKQueryError(
                'An error occurred retrieving automation rule history.',
            ),
        }),
        getNeedsByDate: build.query<{ needsByDate?: string }, { treatmentId: string }>({
            query: ({ treatmentId }) => `needs_by_date/${treatmentId}`,
            providesTags: (_results, _error, { treatmentId }) => [
                { type: TagType.NEEDS_BY_DATE, id: treatmentId },
            ],
            transformResponse: (response: { needs_by_date: string } | null) => ({
                needsByDate: response?.needs_by_date,
            }),
            transformErrorResponse: handleRTKQueryError(
                'An error occurred retrieving needs by date.',
            ),
        }),
        updateNeedsByDate: build.mutation<
            void,
            {
                treatmentId: string;
                body: {
                    payer_id: string;
                    needs_by_date?: string;
                    reason?: string;
                    is_correct: string;
                    referral_id: string;
                    can_meet?: string;
                    know_correct?: string;
                    begin_conversation?: boolean;
                };
            }
        >({
            query: ({ treatmentId, body }) => ({
                url: `needs_by_date/${treatmentId}`,
                method: 'POST',
                body,
            }),
            invalidatesTags: (_results, _error, { treatmentId }) => [
                { type: TagType.NEEDS_BY_DATE, id: treatmentId },
            ],
        }),
        updateFirstFillInProgress: build.mutation<
            void,
            { referralId: string; firstFillInProgress: boolean }
        >({
            query: ({ referralId, firstFillInProgress }) => ({
                url: `first_fill_status/${referralId}`,
                method: 'POST',
                body: {
                    first_fill_in_progress: firstFillInProgress,
                },
            }),
            invalidatesTags: [TagType.REFERRALS],
            transformResponse: () => {
                SuccessToast({ message: 'First fill status updated successfully.' });
            },
            transformErrorResponse: handleRTKQueryError(
                'An error occurred updating First Fill Status.',
            ),
        }),
    }),
});

export const {
    useGetPagedReferralsQuery,
    useGetReferralQuery,
    useGetExpiredReferralsQuery,
    useGetDeclinedReferralsQuery,
    useGetAutomationRuleHistoryQuery,
    useGetNeedsByDateQuery,
    useUpdateNeedsByDateMutation,
    useUpdateFirstFillInProgressMutation,
} = performanceApi;
